import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '@/context/DataContext';

export default function HomePage() {
  const { fetchContent, loading } = useData();
  const sectionsRef = useRef([]);

  useEffect(() => {
    fetchContent('home');
  }, [fetchContent]);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="bg-white" data-testid="home-page">
      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Title */}
        <div 
          ref={(el) => (sectionsRef.current[0] = el)} 
          className="text-center mb-8 reveal"
        >
          <h1 className="text-4xl md:text-5xl font-normal text-[#2c3e50] mb-2">
            Psychotherapeutische Praxis
          </h1>
          <h1 className="text-4xl md:text-5xl font-normal text-[#2c3e50]">
            in Borken und in Münster
          </h1>
        </div>

        {/* Der Weg Image */}
        <div 
          ref={(el) => (sectionsRef.current[1] = el)} 
          className="flex justify-center mb-12 reveal hover-zoom"
        >
          <img 
            src="https://psychotherapie-praxis-langer.de/Bilder/Der%20Weg.jpeg"
            alt="Der Weg"
            className="rounded-lg shadow-lg max-w-full"
            style={{ maxWidth: '800px', width: '100%' }}
            loading="lazy"
          />
        </div>

        {/* Quote */}
        <div 
          ref={(el) => (sectionsRef.current[2] = el)} 
          className="text-center mb-12 max-w-3xl mx-auto reveal"
        >
          <h2 className="text-xl md:text-2xl font-normal text-[#2c3e50] italic leading-relaxed mb-4">
            „Gefühle sind wie Wellen. Wir können sie nicht stoppen wenn sie kommen, aber wir können uns aussuchen, auf welchen wir surfen wollen."
          </h2>
          <p className="text-[#7f8c8d] italic">
            (Jon Kabat Zinn)
          </p>
        </div>

        {/* Introduction Text */}
        <div 
          ref={(el) => (sectionsRef.current[3] = el)} 
          className="text-center mb-12 max-w-3xl mx-auto reveal" 
          style={{ fontSize: '1.15em' }}
        >
          <p className="mb-4">
            <strong>Ich freue mich, dass Sie den Weg zu mir gefunden haben.</strong>
          </p>
          <p className="text-[#2c3e50] mb-4 leading-relaxed">
            Ich bin staatlich anerkannte <strong>Psychologische Psychotherapeutin</strong> mit Schwerpunkt Verhaltenstherapie für Erwachsene und Gruppen. In meiner Praxis im Zentrum von Borken biete ich Verhaltenstherapie sowie Beratung und Coaching an.
          </p>
          <p className="text-[#2c3e50] mb-8 leading-relaxed">
            Auf den folgenden Seiten erhalten Sie Einblicke in die Behandlungsangebote. Wenn Sie weitergehende Fragen haben oder ein unverbindliches Erstgespräch vereinbaren möchten, <strong>kontaktieren Sie mich sehr gerne!</strong>
          </p>
          <div className="mt-8 pt-4">
            <p className="italic text-xl text-[#2c3e50] font-medium">
              Katharina Langer
            </p>
            <p className="text-[#7f8c8d] mt-1">
              Psychologische Psychotherapeutin
            </p>
          </div>
        </div>

        {/* Divider */}
        <hr className="my-12 border-gray-300" />

        {/* Three Columns */}
        <div className="grid md:grid-cols-3 gap-8">
          {/* Katharina Langer */}
          <div 
            ref={(el) => (sectionsRef.current[4] = el)} 
            className="text-center reveal"
            style={{ animationDelay: '0ms' }}
          >
            <h3 className="text-xl font-semibold text-[#2c3e50] mb-4">Katharina Langer</h3>
            <div className="hover-zoom mb-4">
              <img 
                src="https://psychotherapie-praxis-langer.de/Bilder/KatharinaLanger.jpg"
                alt="Katharina Langer"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="text-[#2c3e50] text-sm leading-relaxed">
              Wie kann ich Sie als Psychologische Psychotherapeutin unterstützen? Welche Ausbildung und Erfahrungen ich mitbringe erfahren Sie{' '}
              <Link to="/ueber-mich" className="text-[#3498db] hover:underline hover:text-[#2980b9]">hier.</Link>
            </p>
          </div>

          {/* Therapieangebote */}
          <div 
            ref={(el) => (sectionsRef.current[5] = el)} 
            className="text-center reveal"
            style={{ animationDelay: '100ms' }}
          >
            <h3 className="text-xl font-semibold text-[#2c3e50] mb-4">Therapieangebote</h3>
            <div className="hover-zoom mb-4">
              <img 
                src="https://psychotherapie-praxis-langer.de/Bilder/BueroMuenster.jpg"
                alt="Therapieangebote"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="text-[#2c3e50] text-sm leading-relaxed">
              Sie möchten wissen bei welchen psychischen Problemen ich Ihnen weiterhelfen kann? Dann finden Sie{' '}
              <Link to="/therapieangebote" className="text-[#3498db] hover:underline hover:text-[#2980b9]">hier</Link>{' '}
              meine Arbeitsschwerpunkte.
            </p>
          </div>

          {/* Praxiseinblick */}
          <div 
            ref={(el) => (sectionsRef.current[6] = el)} 
            className="text-center reveal"
            style={{ animationDelay: '200ms' }}
          >
            <h3 className="text-xl font-semibold text-[#2c3e50] mb-4">Praxiseinblick</h3>
            <div className="hover-zoom mb-4">
              <img 
                src="https://psychotherapie-praxis-langer.de/Bilder/IMG_4873.jpg"
                alt="Praxiseinblick"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="text-[#2c3e50] text-sm leading-relaxed">
              Machen Sie sich ein Bild von meiner Praxis. In freundlicher Atmosphäre möchte ich mit Ihnen Gespräche führen, die Sie weiterbringen.{' '}
              <Link to="/praxiseinblick" className="text-[#3498db] hover:underline hover:text-[#2980b9]">Zur Galerie</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
